import { FileWithPath } from "@mantine/dropzone";
import { useCallback, useState } from "react";

import { showErrorToast, showSuccessToast } from "#app/notifications";
import {
  sendOnboardingUploadErrorNotification,
  sendOnboardingUploadNotification,
} from "#shared/infra/onboardingNotification.service";
import { uploadFile } from "#shared/infra/uploadFile.service";
import { FileTypeSchema } from "shared";

interface UploadedFile {
  name: string;
  url: string;
}
interface UseOnboardingUploadReturn {
  uploadFiles: (files: FileWithPath[], email: string, companyId?: string) => Promise<void>;
  isUploading: boolean;
  uploadProgress: number;
  uploadedFiles: UploadedFile[];
  resetUpload: () => void;
}

const yymmdd = () => {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yy}${mm}${dd}`;
};

const normalizeRelPath = (rel: string) =>
  rel
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .split("/")
    .filter((segment) => segment && segment !== ".")
    .map(encodeURI)
    .join("/");

const getRelativePath = (file: FileWithPath) => {
  const rel = file.webkitRelativePath || file.relativePath || file.path || file.name;

  return normalizeRelPath(String(rel));
};

const getRelativeDir = (file: FileWithPath) => {
  const rel = getRelativePath(file);

  if (rel.toLowerCase().endsWith(file.name.toLowerCase())) {
    const dir = rel.slice(0, rel.length - file.name.length);

    return normalizeRelPath(dir);
  }

  const i = rel.lastIndexOf("/");

  return i > -1 ? normalizeRelPath(rel.slice(0, i)) : "";
};

const encodePathSegments = (p: string) => p.split("/").filter(Boolean).map(encodeURI).join("/");

export const useOnboardingUpload = (): UseOnboardingUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const uploadFiles = useCallback(
    async (files: FileWithPath[], email: string, companyId?: string) => {
      if (files.length === 0) {
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);
      setUploadedFiles([]);

      try {
        const emailPath = encodePathSegments(
          email.trim().replace(/@/g, "__at__").replace(/\./g, "__dot__"),
        );

        const today = yymmdd();

        const companyPath = companyId
          ? `${encodePathSegments(companyId)}/raw/${today}`
          : "onboarding";

        let uploaded = 0;

        const results: UploadedFile[] = [];

        for (const file of files) {
          const relDir = getRelativeDir(file);

          const folderParts = [emailPath, relDir].filter(Boolean).join("/");

          const { fileUrl } = await uploadFile({
            file,
            dangerousFolderPathOverride: `${companyPath}/${encodePathSegments(folderParts)}/`,
            callback: (progress: number) => {
              const total = ((uploaded + progress / 100) / files.length) * 100;
              setUploadProgress(Math.round(total));
            },
            type: FileTypeSchema.enum.ONBOARDING,
            isUserConnected: false,
          });

          results.push({
            name: file.name,
            url: fileUrl,
          });

          uploaded += 1;
          setUploadProgress(Math.round((uploaded / files.length) * 100));
        }

        setUploadedFiles(results);
        setUploadProgress(100);

        const s3Folder = companyId
          ? `${encodePathSegments(companyId)}/raw/${today}/${emailPath}`
          : `onboarding/${emailPath}`;

        await sendOnboardingUploadNotification({
          email,
          companyId,
          s3Folder,
        });

        showSuccessToast({
          title: "Fichiers téléchargés avec succès",
          message: `${results.length} fichier(s) téléchargé(s)`,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";

        await sendOnboardingUploadErrorNotification({
          email,
          companyId,
          error: errorMessage,
        });

        showErrorToast({
          title: "Erreur lors du téléchargement",
          message: "Veuillez réessayer",
        });
        setUploadProgress(0);
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [],
  );

  const resetUpload = useCallback(() => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadedFiles([]);
  }, []);

  return {
    uploadFiles,
    isUploading,
    uploadProgress,
    uploadedFiles,
    resetUpload,
  };
};
